import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Clock, MapPin, MessageSquareText, PackageCheck, ShieldCheck, Truck } from "lucide-react";
import { Seo, breadcrumbSchema, productSchema, faqSchema } from "@/components/site/Seo";
import { PageHero, LifestyleImage } from "@/components/site/PageHero";
import { ProductPlaceholder } from "@/components/site/ProductPlaceholder";
import { ProductCard } from "@/components/site/ProductCard";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { FaqAccordion } from "@/components/site/FaqAccordion";
import { Disclaimer } from "@/components/site/Disclaimer";
import { Reveal } from "@/components/site/Reveal";
import { getProduct, VERIFIED_FIELDS } from "@/data/products";
import { IMAGES, BUSINESS } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

const ACCENT_IMAGE = { ocean: "oceanDeep", leaves: "leaves", honey: "honey", beach: "beach", hibiscus: "hibiscus" };

export default function ProductPage({ slug }) {
  const product = getProduct(slug);
  if (!product) return <Navigate to="/products" replace />;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Our Products", path: "/products" },
    { name: product.name, path: product.path },
  ];
  const accent = IMAGES[ACCENT_IMAGE[product.accent]] || IMAGES.botanical;
  const related = product.related.map(getProduct).filter(Boolean);
  const isSeaMoss = slug === "sea-moss-gel";
  const anyVerified = VERIFIED_FIELDS.some((f) => product.verified[f.key]);

  const scrollToEnquire = () => {
    trackEvent("product_enquiry_click", { product: slug, location: "product-hero" });
    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => document.getElementById(`product-enquiry-first_name`)?.focus({ preventScroll: true }), 500);
  };

  return (
    <>
      <Seo
        title={product.seo.title}
        description={product.seo.description}
        path={product.path}
        type="product"
        image={`https://images.unsplash.com/${accent.id}?auto=format&fit=crop&w=1200&q=70`}
        jsonLd={[breadcrumbSchema(crumbs), productSchema(product), faqSchema(product.faqs)]}
      />

      <PageHero
        crumbs={crumbs}
        eyebrow={
          <>
            {product.category}
            {product.featured ? <span className="badge-featured ml-2">Featured</span> : null}
          </>
        }
        title={product.h1}
        aside={
          <div className="card-soft p-3">
            <ProductPlaceholder product={product} size="lg" className="aspect-square sm:aspect-[4/3] lg:aspect-square" />
          </div>
        }
      >
        <div className="mt-5 max-w-2xl space-y-4">
          {product.intro.map((t) => (
            <p key={t} className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t}
            </p>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button type="button" onClick={scrollToEnquire} className="btn btn-primary px-6" data-testid="product-hero-enquire-button">
            <MessageSquareText className="h-4 w-4" aria-hidden="true" />
            {product.enquiryCta}
          </button>
          <WhatsAppButton message={product.whatsappMessage} product={slug} label="WhatsApp Uhlumo Organics" testid="product-hero-whatsapp-button" location="product-hero" />
        </div>
        <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[color:var(--u-green)]" aria-hidden="true" /> Supplied from Gugulethu, Cape Town
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-[color:var(--u-green)]" aria-hidden="true" /> Price on request
          </li>
        </ul>
      </PageHero>

      {/* What is it */}
      <section className="section" aria-labelledby="what-heading">
        <div className="container-site grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-14">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow">Product overview</p>
            <h2 id="what-heading" className="h-section mt-3">
              {product.whatIs.heading}
            </h2>
            <div className="mt-5 space-y-4">
              {product.whatIs.paragraphs.map((t) => (
                <p key={t} className="text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                  {t}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5">
            <LifestyleImage id={accent.id} alt={accent.alt} className="aspect-[4/3]" />
          </Reveal>
        </div>
      </section>

      {/* Product information table */}
      <section className="section surface-sage" aria-labelledby="info-heading" data-testid="product-info-section">
        <div className="container-site grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Label information</p>
            <h2 id="info-heading" className="h-section mt-3">
              Product information
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
              We only publish details that have been verified against the actual {product.name} packaging and supplier information. Details marked “to be confirmed” are available on enquiry.
            </p>
            <p className="mt-4 flex items-start gap-2 text-sm text-[#3d4a42]">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--u-blue)]" aria-hidden="true" />
              Always read the label on your product before use.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="card-soft overflow-hidden">
              <table className="w-full text-left text-sm" data-testid="product-info-table">
                <caption className="sr-only">{product.name} product information</caption>
                <thead className="bg-[#F4F7F2]">
                  <tr>
                    <th scope="col" className="px-5 py-3.5 font-semibold sm:px-6">
                      Detail
                    </th>
                    <th scope="col" className="px-5 py-3.5 font-semibold sm:px-6">
                      Information
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <th scope="row" className="px-5 py-4 font-medium sm:px-6">
                      Product
                    </th>
                    <td className="px-5 py-4 sm:px-6">{product.fullName || product.name}</td>
                  </tr>
                  <tr className="border-t">
                    <th scope="row" className="px-5 py-4 font-medium sm:px-6">
                      Product type
                    </th>
                    <td className="px-5 py-4 sm:px-6">{product.category}</td>
                  </tr>
                  {VERIFIED_FIELDS.map((f) => (
                    <tr key={f.key} className="border-t">
                      <th scope="row" className="px-5 py-4 font-medium sm:px-6">
                        {f.label}
                      </th>
                      <td className="px-5 py-4 sm:px-6">
                        {product.verified[f.key] ? (
                          <span>{product.verified[f.key]}</span>
                        ) : (
                          <span className="inline-flex items-center gap-2 text-muted-foreground" data-testid={`product-info-${f.key}-tbc`}>
                            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                            To be confirmed — please enquire
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!anyVerified ? <p className="mt-3 text-xs text-muted-foreground">Label verification in progress. Information will be updated as soon as it is confirmed.</p> : null}
          </div>
        </div>
      </section>

      {/* Ordering & delivery */}
      <section className="section" aria-labelledby="ordering-heading">
        <div className="container-site">
          <h2 id="ordering-heading" className="h-section">
            {isSeaMoss ? "Where to buy Sea Moss Gel in Cape Town" : "Ordering and delivery"}
          </h2>
          <p className="lead mt-3 max-w-3xl">
            {isSeaMoss
              ? `Uhlumo Organics is based in ${BUSINESS.address.suburb}, ${BUSINESS.address.city}. Send us an enquiry or WhatsApp us to check available sizes and pricing, and to arrange delivery or collection.`
              : `Orders for ${product.name} are placed by enquiry. We confirm availability, pricing and delivery or collection options with you directly.`}
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { icon: MessageSquareText, t: "Enquire", b: "Use the form below or WhatsApp us on 065 377 8765." },
              { icon: PackageCheck, t: "Confirm", b: "We share available sizes, label details and current pricing." },
              { icon: Truck, t: "Deliver or collect", b: "Delivery and collection options are confirmed per order." },
            ].map(({ icon: Icon, t, b }) => (
              <div key={t} className="card-soft p-6">
                <Icon className="h-6 w-6 text-[color:var(--u-green)]" aria-hidden="true" />
                <h3 className="mt-4 text-lg font-semibold">{t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section id="enquire" className="section scroll-mt-24 surface-sand" aria-labelledby="enquire-heading" data-testid="product-enquiry-section">
        <div className="container-site grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Pricing enquiry</p>
            <h2 id="enquire-heading" className="h-section mt-3">
              {product.enquiryCta}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">Ask about available sizes, ingredients and label information, current prices, delivery and ordering.</p>
            <div className="mt-6 rounded-2xl bg-white p-5">
              <p className="text-sm font-semibold">Prefer WhatsApp?</p>
              <p className="mt-1 text-sm text-muted-foreground">We’ll pre-fill a message about {product.name} for you.</p>
              <WhatsAppButton message={product.whatsappMessage} product={slug} label="WhatsApp Uhlumo Organics" className="btn btn-whatsapp mt-4 w-full" testid="product-enquiry-whatsapp-button" location="product-enquiry" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="card-soft p-6 sm:p-8">
              <EnquiryForm defaultProduct={slug} idPrefix="product-enquiry" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" aria-labelledby="pfaq-heading" data-testid="product-faq-section">
        <div className="container-site grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">FAQs</p>
            <h2 id="pfaq-heading" className="h-section mt-3">
              {product.name} questions
            </h2>
            <Link to="/faqs" className="link-accent mt-5 inline-block text-sm">
              More FAQs →
            </Link>
          </div>
          <div className="space-y-8 lg:col-span-8">
            <FaqAccordion faqs={product.faqs} idPrefix="product-faq" />
            <Disclaimer />
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="section surface-sage" aria-labelledby="related-heading" data-testid="related-products">
        <div className="container-site">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <h2 id="related-heading" className="h-section">
              Related products
            </h2>
            <Link to="/products" className="link-accent inline-flex items-center gap-1.5 text-sm">
              View all products <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
